import {MoonOutlined, SunOutlined} from '@ant-design/icons';
import type {MenuProps} from 'antd';
import {Avatar, Button, Dropdown, Flex, theme, Tooltip, Typography} from 'antd';
import SnackBar from 'Components/SnackBar';
import {globalConfig} from 'Config/config';
import {useCSContext} from 'Context/ContentStackContext';
import {useData} from 'Context/DataContext';
import {useThemeMode} from 'Context/ThemeModeContext';
import {useSnackBar} from 'Hooks';
import iconSize from 'Theme/Icon';
import {LOGIN, QUERY_DASHBOARD, SEARCH} from 'Utils/constants/Paths';
import createAvatar from 'Utils/helpers/CreateAvatar';
import getAvatarColor from 'Utils/helpers/GetAvatarColor';
import {stopTokenRefreshTimer} from 'Utils/helpers/RefreshTokenScheduler';
import React, {Fragment, useState} from 'react';
import {useSelector} from 'react-redux';
import {Link, useLocation} from 'react-router-dom';
import LogoutBackdrop from './LogoutBackdrop';
import styles, {activeLinkStyle, iconBtnStyle} from './styles';

const {Text} = Typography;

const Header = () => {
  const {header, artifacts} = useCSContext();
  const {pathname} = useLocation();
  const {setStoredIdentifier} = useData();
  const [error, setError] = useState('');
  const [isSnackBarOpen, closeSnackBar] = useSnackBar(error);
  const [loading, setLoading] = useState(false);
  const agentName = useSelector((state: any) => {
    return state?.login.name;
  });
  const roleConfig = useSelector((state: any) => state?.login?.roleConfig);
  const {token}: any = theme.useToken();
  const {mode, setMode} = useThemeMode();

  const logoutAgent = async () => {
    setLoading(true);
    try {
      stopTokenRefreshTimer();
      sessionStorage.clear();
      localStorage.clear();
      window.location.replace(
        `${globalConfig._config.internalSSOConfig.REACT_APP_KEYCLOAK_ENDPOINT}/realms/${globalConfig._config.internalSSOConfig.REACT_APP_REALM}/protocol/openid-connect/logout?redirect_uri=${window.location.href}`,
      );
    } catch (err) {
      setError('Something went wrong');
      console.warn(err);
    }
    setLoading(false);
  };

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      label: <Text>{header.logout_button_title}</Text>,
      icon: (
        <img
          src={artifacts.logout_icon}
          style={{height: 25, width: 25}}
          alt={header.logout_button_title}
        />
      ),
      onClick: logoutAgent,
    },
  ];

  return (
    <Fragment>
      <Flex
        justify="space-between"
        style={{
          ...styles.main,
          borderColor: token.colorBorder,
          backgroundColor: token.colorBgContainer,
        }}>
        <Flex style={styles.containerLeft}>
          <img src={artifacts.logo} alt="logo" style={{width: '150px'}} />
          <span role="heading" aria-level={1}>
            <Text style={{fontSize: token.fontSizeHeading5}}>{header.heading}</Text>
          </span>
        </Flex>
        {pathname !== LOGIN && (
          <Flex style={styles.containerRight}>
            <Link
              //resets the input field - only when new member is clicked, not on back button
              onClick={() => setStoredIdentifier('')}
              to={SEARCH}
              style={{
                ...styles.wrapperIconText,
                ...(pathname === SEARCH && activeLinkStyle(token.colorPrimary)),
              }}>
              <img src={artifacts.search_icon} style={iconSize} alt="New Search" />
              <Text style={pathname === SEARCH ? activeLinkStyle(token.colorPrimary) : undefined}>
                Member Search
              </Text>
            </Link>
            {roleConfig?.queryDashboard && (
              <Link
                to={QUERY_DASHBOARD}
                style={{
                  ...styles.wrapperIconText,
                  ...(pathname === QUERY_DASHBOARD && activeLinkStyle(token.colorPrimary)),
                }}>
                <img
                  src={artifacts.query_dashboard_icon}
                  style={iconSize}
                  alt="Query Dashboard icon"
                />
                <Text
                  style={
                    pathname === QUERY_DASHBOARD ? activeLinkStyle(token.colorPrimary) : undefined
                  }>
                  {header.query_dashboard_title}
                </Text>
              </Link>
            )}
            <Flex gap={8}>
              <Tooltip title="Light mode">
                <Button
                  type={mode === 'light' ? 'primary' : 'default'}
                  shape="circle"
                  aria-label="Switch to light mode"
                  aria-pressed={mode === 'light'}
                  icon={<SunOutlined />}
                  onClick={() => setMode('light')}
                  style={
                    mode === 'light'
                      ? undefined
                      : iconBtnStyle(token.colorBorderSecondary, token.colorBgContainer)
                  }
                />
              </Tooltip>
              <Tooltip title="Dark mode">
                <Button
                  type={mode === 'dark' ? 'primary' : 'default'}
                  shape="circle"
                  aria-label="Switch to dark mode"
                  aria-pressed={mode === 'dark'}
                  icon={<MoonOutlined />}
                  onClick={() => setMode('dark')}
                  style={
                    mode === 'dark'
                      ? undefined
                      : iconBtnStyle(token.colorBorderSecondary, token.colorBgContainer)
                  }
                />
              </Tooltip>
            </Flex>
            <Dropdown menu={{items: profileMenuItems}} trigger={['click']}>
              <Flex align="center" style={{...styles.wrapperIconText, cursor: 'pointer'}}>
                <Avatar
                  style={{
                    ...iconSize,
                    backgroundColor: getAvatarColor(agentName),
                    fontSize: '14px',
                    fontWeight: 600,
                  }}>
                  {createAvatar(agentName)}
                </Avatar>
                <Text>{agentName}</Text>
              </Flex>
            </Dropdown>
          </Flex>
        )}
        <SnackBar
          isSnackBarOpen={isSnackBarOpen}
          closeSnackBar={closeSnackBar}
          errorMessage={error}
        />
      </Flex>
      {loading && <LogoutBackdrop text="Logging out" />}
    </Fragment>
  );
};

export default Header;
